<script setup>
import { computed } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { useTrack } from 'dashboard/composables';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { COPILOT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  isLastMessage: {
    type: Boolean,
    default: false,
  },
  message: {
    type: Object,
    required: true,
  },
  createdAt: {
    type: [Number, String],
    default: null,
  },
  conversationInboxType: {
    type: String,
    required: true,
  },
});

const hasEmptyMessageContent = computed(() => !props.message?.content);
const showUseButton = computed(
  () =>
    !hasEmptyMessageContent.value &&
    props.message.reply_suggestion &&
    props.isLastMessage
);
const messageContent = computed(() => {
  const formatter = new MessageFormatter(props.message.content);
  return formatter.formattedMessage;
});
const formattedTime = computed(() => {
  const value = Number(props.createdAt);
  if (!Number.isFinite(value) || value <= 0) return '';

  const date = new Date(value > 100000000000 ? value : value * 1000);
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
});
const insertIntoRichEditor = computed(() =>
  [INBOX_TYPES.WEB, INBOX_TYPES.EMAIL].includes(props.conversationInboxType)
);

const useCopilotResponse = () => {
  if (insertIntoRichEditor.value) {
    emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, props.message?.content);
  } else {
    emitter.emit(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, props.message?.content);
  }
  useTrack(COPILOT_EVENTS.USE_CAPTAIN_RESPONSE);
};
</script>

<template>
  <div
    class="self-start box-border w-fit max-w-[86%] ltr:mr-8 rtl:ml-8 rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm border border-n-weak bg-n-surface-1 px-3 py-2.5 text-n-slate-12"
  >
    <span v-if="hasEmptyMessageContent" class="text-n-ruby-11">
      {{ $t('CAPTAIN.COPILOT.EMPTY_MESSAGE') }}
    </span>
    <div
      v-else
      v-dompurify-html="messageContent"
      class="prose prose-sm dark:prose-invert max-w-none break-words text-n-slate-12"
    />
    <div v-if="showUseButton" class="flex flex-row mt-1">
      <Button
        :label="$t('CAPTAIN.COPILOT.USE')"
        faded
        sm
        slate
        @click="useCopilotResponse"
      />
    </div>
    <time
      v-if="formattedTime"
      class="mt-2 block text-left text-xs leading-4 text-n-slate-10"
    >
      {{ formattedTime }}
    </time>
  </div>
</template>
